// KK (Kalite Kontrol) paneli (sağ rail) — spec §7.1. Tasarım token'ları Review.tsx'tekilerle aynı.
// Reviewer library promptu seçer → KK başlatır → section.qcStatus yaşam döngüsünü yansıtır
// (null/queued/running/ready/failed). Canlı güncelleme SSE + polling ile Review.tsx tarafında.
import { useEffect, useState } from "react";
import type { QcPromptDto, ReviewSection } from "@mahir/shared";
import { fetchQcPrompts, startQc } from "../../api/qc";

interface QcPanelProps {
  projectId: string;
  section: ReviewSection;
  hasUnsavedChanges: boolean;
  onStarted: () => void; // parent loadSections + polling başlatır
}

export function QcPanel({
  projectId,
  section,
  hasUnsavedChanges,
  onStarted,
}: QcPanelProps): JSX.Element | null {
  const [prompts, setPrompts] = useState<QcPromptDto[] | null>(null);
  const [selectedId, setSelectedId] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void fetchQcPrompts()
      .then((r) => {
        setPrompts(r.prompts);
        if (r.prompts.length > 0) setSelectedId(r.prompts[0]!.id);
      })
      .catch(() => setPrompts([]));
  }, []);

  if (section.reviewStatus === "approved" || !section.content) return null;
  const qcBusy = section.qcStatus === "queued" || section.qcStatus === "running";

  const run = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await startQc(projectId, section.sectionCode, selectedId);
      onStarted();
    } catch (e) {
      setError(e instanceof Error ? e.message : "KK başlatılamadı");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6">
      <h3
        className="mb-2 text-xs uppercase tracking-wider"
        style={{ color: "var(--color-text-3)" }}
      >
        🤖 Kalite Kontrol
      </h3>
      {prompts !== null && prompts.length === 0 && (
        <p className="text-xs" style={{ color: "var(--color-text-3)" }}>
          Aktif KK promptu yok — yöneticinize başvurun.
        </p>
      )}
      {prompts !== null && prompts.length > 0 && !qcBusy && (
        <>
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            className="w-full mb-2 p-2 rounded border text-xs"
            style={{ borderColor: "var(--color-border)" }}
          >
            {prompts.map((p) => (
              <option key={p.id} value={p.id} title={p.description ?? undefined}>
                {p.promptType === "critique" ? "🔍" : "✏️"} {p.name}
              </option>
            ))}
          </select>
          <button
            onClick={() => void run()}
            disabled={busy || hasUnsavedChanges || section.qcStatus === "ready"}
            title={
              hasUnsavedChanges
                ? "Önce kaydet"
                : section.qcStatus === "ready"
                  ? "Önce mevcut sonuca karar verin"
                  : undefined
            }
            className="w-full px-3 py-2 rounded-md text-sm disabled:opacity-50"
            style={{
              background: "var(--color-primary)",
              color: "var(--color-surface)",
              fontWeight: 600,
            }}
          >
            KK Çalıştır
          </button>
        </>
      )}
      {qcBusy && (
        <p className="text-xs animate-pulse" style={{ color: "var(--color-text-2)" }}>
          ⏳ Kalite kontrol çalışıyor… (~1-2 dk sürebilir)
        </p>
      )}
      {section.qcStatus === "failed" && (
        <div className="text-xs" style={{ color: "var(--color-error)" }}>
          {section.qcFailureMessage ?? "KK başarısız"}
          <button onClick={() => void run()} className="block mt-1 underline">
            Tekrar Dene
          </button>
        </div>
      )}
      {/* SEAM (Task 16/17): qcStatus="ready" olduğunda asıl sonuç UI'ı ORTA panelde mount edilir —
          rewrite → yan yana önizleme + reddet modalı (Task 16), critique → sorun listesi paneli
          (Task 17). Panel burada yalnızca özet durum gösterir; preview/critique BURADA implemente
          EDİLMEZ. Mount koşulu: section.qcStatus === "ready" + section.qcMeta.promptType. */}
      {section.qcStatus === "ready" && (
        <p className="text-xs" style={{ color: "var(--color-success)" }}>
          {section.qcMeta?.promptType === "critique"
            ? "Sorun listesi hazır"
            : "Aday hazır — karşılaştırmayı inceleyin"}
        </p>
      )}
      {error && (
        <p className="text-xs mt-1" style={{ color: "var(--color-error)" }}>
          {error}
        </p>
      )}
    </div>
  );
}
